"use client";

import React, { useEffect, useMemo, useRef, useState } from "react";
import Link from "next/link";
import { usePathname, useSearchParams } from "next/navigation";

import { searchContent, type BrightSearchPost } from "@/lib/search";

import SafeHtml from "@/components/ui/SafeHtml";
import Section from "@/components/ui/Section";
import Wrapper from "@/components/ui/Wrapper";
import { useLayoutData } from "@/components/layout/LayoutDataProvider";
import { ArrowIcon, SpinnerIcon } from "@/components/ui/Icons";

type UnknownRecord = Record<string, unknown>;

function isRecord(value: unknown): value is UnknownRecord {
  return typeof value === "object" && value !== null;
}

function getString(obj: unknown, key: string): string | undefined {
  if (!isRecord(obj)) return undefined;
  const v = obj[key];
  return typeof v === "string" ? v : undefined;
}

function getRecord(obj: unknown, key: string): UnknownRecord | undefined {
  if (!isRecord(obj)) return undefined;
  const v = obj[key];
  return isRecord(v) ? v : undefined;
}

export default function SearchResultsClient({
  lang,
  basePath,
  perPage = 12,
}: {
  lang: string;
  basePath: string;
  perPage?: number;
}) {
  const searchParams = useSearchParams();
  const pathname = usePathname();

  // URL state
  const s = (searchParams?.get("s") ?? "").trim();
  const page = Math.max(1, Number(searchParams?.get("page") ?? "1") || 1);

  // Translated/global text
  const { global_text } = useLayoutData() as {
    global_text?: {
      search?: {
        search_heading?: string;
        search_results_title?: string;
        search_no_query?: string;
        search_no_results?: string;
        search_results_for?: string;
        pagination_previous?: string;
        pagination_next?: string;
      };
    };
  };

  const t = global_text?.search;

  // Local state
  const [loading, setLoading] = useState(false);
  const [items, setItems] = useState<BrightSearchPost[]>([]);
  const [totalPages, setTotalPages] = useState(1);
  const [error, setError] = useState<string | null>(null);

  const debug = useMemo(
    () => ({
      pathname,
      basePath,
      lang,
      s,
      page,
      perPage,
      rawQuery: searchParams?.toString() ?? "",
    }),
    [pathname, basePath, lang, s, page, perPage, searchParams]
  );

  // Scroll to top ONLY when page changes (pagination click)
  const prevPageRef = useRef(page);
  useEffect(() => {
    const prev = prevPageRef.current;
    prevPageRef.current = page;

    if (!s) return;
    if (page === prev) return;

    const el = document.getElementById("search-top");
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    else window.scrollTo({ top: 0, behavior: "smooth" });
  }, [page, s]);

  // Fetch results
  useEffect(() => {
    if (!s) {
      setItems([]);
      setTotalPages(1);
      setError(null);
      setLoading(false);
      return;
    }

    let cancelled = false;

    async function run() {
      setLoading(true);
      setError(null);

      const res = await searchContent({ s, lang, page, perPage });

      if (cancelled) return;

      if (!res) {
        setItems([]);
        setTotalPages(1);
        setError("Search failed");
        setLoading(false);
        return;
      }

      setItems(Array.isArray(res.posts) ? res.posts : []);
      setTotalPages(Number(res.pagination?.max_pages ?? 1) || 1);
      setLoading(false);
    }

    run();

    return () => {
      cancelled = true;
    };
  }, [debug, s, lang, page, perPage]);

  /** ---------------- Helpers: title + description ---------------- */

  const stripHtml = (html: string) =>
    html.replace(/<[^>]+>/g, " ").replace(/\s+/g, " ").trim();

  const makeSnippet = (html: string, max = 170) => {
    const text = stripHtml(html);
    return text.length > max ? text.slice(0, max).trim() + "…" : text;
  };

  // Your endpoint returns raw WP-ish keys (ID/post_title/post_excerpt/post_content/acf)
  // but we also support WP REST-ish + Yoast/RankMath if they ever appear.
  const getBestDescription = (post: BrightSearchPost): string | null => {
    const p: unknown = post;

    // 1) Yoast
    const yoastHead = getRecord(p, "yoast_head_json");
    const yoast = yoastHead ? getString(yoastHead, "description") : undefined;
    if (typeof yoast === "string" && yoast.trim()) return yoast.trim();

    // 2) RankMath / common SEO keys
    const rank = getString(p, "rank_math_description");
    if (typeof rank === "string" && rank.trim()) return rank.trim();

    const seoDesc = getString(p, "seo_description");
    if (typeof seoDesc === "string" && seoDesc.trim()) return seoDesc.trim();

    // 3) ACF SEO patterns (if your ACF includes SEO description)
    const acf = isRecord(post.acf) ? post.acf : getRecord(p, "acf");

    if (acf) {
      const acfSeo = getString(acf, "seo_description");
      if (typeof acfSeo === "string" && acfSeo.trim()) return acfSeo.trim();

      const acfDesc = getString(acf, "description");
      if (typeof acfDesc === "string" && acfDesc.trim()) return acfDesc.trim();
    }

    // 4) WordPress excerpt (your endpoint: post_excerpt)
    const postExcerpt = getString(p, "post_excerpt");
    if (typeof postExcerpt === "string") {
      const cleaned = stripHtml(postExcerpt);
      if (cleaned) return cleaned;
    }

    // 5) WP REST excerpt (excerpt.rendered)
    const excerpt = getRecord(p, "excerpt");
    const excerptRendered = excerpt ? getString(excerpt, "rendered") : undefined;
    if (typeof excerptRendered === "string") {
      const cleaned = stripHtml(excerptRendered);
      if (cleaned) return cleaned;
    }

    // 6) Fallback to content (your endpoint: post_content)
    const postContent = getString(p, "post_content");
    if (typeof postContent === "string") {
      const snippet = makeSnippet(postContent);
      if (snippet) return snippet;
    }

    // 7) WP REST content.rendered
    const content = getRecord(p, "content");
    const contentRendered = content ? getString(content, "rendered") : undefined;
    if (typeof contentRendered === "string") {
      const snippet = makeSnippet(contentRendered);
      if (snippet) return snippet;
    }

    return null;
  };

  const getCleanTitle = (post: BrightSearchPost) => {
    const p: unknown = post;
    const raw = (
      getString(p, "post_title") ??
      post.post_title ??
      post.title?.rendered ??
      ""
    ).toString();
    return stripHtml(raw) || "Untitled";
  };

  /** ---------------- Header (PageHeader-like) ---------------- */

  const Header = ({
    label = "",
    title = "",
    subtitle = "",
  }: {
    label?: string;
    title?: string;
    subtitle?: string;
  }) => {
    if (!label && !title && !subtitle) return null;

    return (
      <div id="search-top" className="min-w-0 text-center flex flex-col items-center gap-4">
        {label && (
          <div className="inline-flex rounded-full bg-fuji-grey px-4 py-2">
            <div className="uppercase tracking-wide font-semibold text-body-2">
              <SafeHtml content={label} />
            </div>
          </div>
        )}

        {title && (
          <h1 className="text-title-1 font-semibold">
            <SafeHtml content={title} />
          </h1>
        )}

        {subtitle && (
          <div className="text-title-4 font-semibold">
            <SafeHtml content={subtitle} />
          </div>
        )}
      </div>
    );
  };

  const label = t?.search_heading ?? "";
  const title = t?.search_results_title ?? "";
  const subtitle = s
    ? `${t?.search_results_for ?? ""} <span class="font-semibold">“${s}”</span>`
    : "";

  /** ---------------- UI states ---------------- */

  if (!s) {
    return (
      <Section>
        <Wrapper>
          <Header label={label} title={title} subtitle={t?.search_no_query ?? ""} />
        </Wrapper>
      </Section>
    );
  }

  if (error) {
    return (
      <Section>
        <Wrapper>
          <Header label={label} title={title} subtitle={subtitle} />
          <div className="text-center mt-8">
            <p className="m-0">{error}</p>
          </div>
        </Wrapper>
      </Section>
    );
  }

  return (
    <Section>
      <Wrapper>
        <Header label={label} title={title} subtitle={subtitle} />

        {/* Loading spinner replaces the "no results" area */}
        {loading ? (
          <div className="max-w-content mx-auto text-center mt-8 flex items-center justify-center gap-3">
            <SpinnerIcon className="text-fuji-black" />
          </div>
        ) : !items.length ? (
          <div className="max-w-content mx-auto text-center mt-8">
            {/* IMPORTANT: don't wrap SafeHtml in <p> (hydration-safe) */}
            <div className="m-0 [&_p]:m-0 [&_*]:m-0">
              <SafeHtml content={t?.search_no_results ?? ""} />
            </div>
          </div>
        ) : (
          <div className="max-w-content mx-auto mt-8">
            <div className="divide-y divide-fuji-grey-dark/40">
              {items.map((post) => {
                const p: unknown = post;

                // href like old site
                let href = "";
                if (typeof post?.link === "string" && post.link) {
                  try {
                    const u = new URL(post.link, "https://example.local");
                    href = u.pathname || "";
                  } catch {
                    /* ignore */
                  }
                }

                if (!href) {
                  const slug = (getString(p, "slug") ?? post.slug ?? "")
                    .toString()
                    .replace(/^\/+|\/+$/g, "");
                  href = `/${slug}`;
                }

                if (!href.startsWith("/")) href = `/${href}`;

                const cleanTitle = getCleanTitle(post);
                const description = getBestDescription(post);

                const idNum =
                  isRecord(p) && typeof p["ID"] === "number" ? p["ID"] : undefined;

                return (
                  <article key={`${idNum ?? post.id ?? href}`} className="py-4">
                    <h3 className="mt-0 mb-2 text-title-4 font-medium">
                      <Link href={href} className="no-underline hover:underline">
                        {cleanTitle}
                      </Link>
                    </h3>

                    {/* description/excerpt/seo */}
                    {description && (
                      <p className="m-0 text-body text-fuji-black/80">{description}</p>
                    )}
                  </article>
                );
              })}
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <nav
                className="max-w-content mx-auto mt-8 flex items-center justify-center gap-4 font-semibold"
                aria-label="Pagination"
              >
                {page > 1 && (
                  <Link
                    scroll={false}
                    href={`${basePath}?s=${encodeURIComponent(s)}&page=${page - 1}`}
                    aria-label={t?.pagination_previous}
                    className="inline-flex items-center gap-2 no-underline hover:underline"
                  >
                    <ArrowIcon direction="left" />
                    <span className="sr-only">{t?.pagination_previous}</span>
                  </Link>
                )}

                {Array.from({ length: totalPages }, (_, i) => i + 1).map((n) => (
                  <Link
                    key={n}
                    scroll={false}
                    href={`${basePath}?s=${encodeURIComponent(s)}&page=${n}`}
                    aria-current={n === page ? "page" : undefined}
                    className={n === page ? "underline" : "no-underline hover:underline"}
                  >
                    {n}
                  </Link>
                ))}

                {page < totalPages && (
                  <Link
                    scroll={false}
                    href={`${basePath}?s=${encodeURIComponent(s)}&page=${page + 1}`}
                    aria-label={t?.pagination_next}
                    className="inline-flex items-center gap-2 no-underline hover:underline"
                  >
                    <span className="sr-only">{t?.pagination_next}</span>
                    <ArrowIcon direction="right" />
                  </Link>
                )}
              </nav>
            )}
          </div>
        )}
      </Wrapper>
    </Section>
  );
}
